import React, { useState } from "react";

type GitDiffProps = {
  oldText: string;
  newText: string;
};

interface DiffLine {
  type: 'context' | 'added' | 'removed';
  content: string;
}

function computeDiff(oldText: string, newText: string): DiffLine[] {
  const oldLines = oldText.split('\n');
  const newLines = newText.split('\n');
  
  // Simple LCS-based diff
  const m = oldLines.length;
  const n = newLines.length;
  
  // Build LCS table
  const dp: number[][] = Array.from({ length: m + 1 }, () => Array(n + 1).fill(0));
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      if (oldLines[i - 1] === newLines[j - 1]) {
        dp[i][j] = dp[i - 1][j - 1] + 1;
      } else {
        dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1]);
      }
    }
  }
  
  // Backtrack to find diff lines
  const result: DiffLine[] = [];
  let i = m;
  let j = n;
  
  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && oldLines[i - 1] === newLines[j - 1]) {
      result.unshift({ type: 'context', content: oldLines[i - 1] });
      i--;
      j--;
    } else if (j > 0 && (i === 0 || dp[i][j - 1] >= dp[i - 1][j])) {
      result.unshift({ type: 'added', content: newLines[j - 1] });
      j--;
    } else {
      result.unshift({ type: 'removed', content: oldLines[i - 1] });
      i--;
    }
  }
  
  return result;
}

export const GitDiff: React.FC<GitDiffProps> = ({ oldText, newText }) => {
  const diffLines = computeDiff(oldText, newText);
  
  if (diffLines.length === 0) {
    return <div className="git-diff-empty">No changes</div>;
  }
  
  // Limit display to first 50 lines for performance
  const maxLines = 50;
  const [expanded, setExpanded] = useState(false);
  const displayLines = expanded ? diffLines : diffLines.slice(0, maxLines);
  const hasMore = diffLines.length > maxLines;
  
  return (
    <div className="git-diff">
      {displayLines.map((line, idx) => (
        <div key={idx} className={`git-diff-line git-diff-${line.type}`}>
          <span className="git-diff-marker">{
            line.type === 'added' ? '+' : 
            line.type === 'removed' ? '-' : ' '
          }</span>
          <span className="git-diff-content">{line.content}</span>
        </div>
      ))}
      {hasMore && (
        <button
          className="git-diff-truncated"
          onClick={() => setExpanded(!expanded)}
          title={expanded ? 'Show less' : `Show all ${diffLines.length} lines`}
        >
          {expanded ? '▲ Show less' : `▼ ... ${diffLines.length - maxLines} more lines`}
        </button>
      )}
    </div>
  );
};

export default GitDiff;
